<script setup lang="ts">
import { AxiosError } from 'axios';
import { throttle } from 'lodash';
import { computed, ref } from 'vue';
import { toast } from 'vue3-toastify';

import IconCalendarCheck from '~/assets/icons/IconCalendarCheck.vue';
import IconClock from '~/assets/icons/IconClock.vue';
import IconClockAlert from '~/assets/icons/IconClockAlert.vue';
import IconFileCheck from '~/assets/icons/IconFileCheck.vue';
import StatusCard from '~/components/shared/StatusCard.vue';
import { FinancingFlowApi } from '~/services/financing-flow-api/financing-flow.types';
import utils from '~/services/financing-flow-api/financing-flow.utils';

import type { UseFormalizationInfoData } from '../../queries/contract-formalization/useFormalizationInfo';
import type { UseFinancingDetailsData } from '../../queries/useFinancingDetails';

import useResendSignature from '../../queries/contract-formalization/useResendSignature';
import FinancingActions from '../FinancingActions.vue';
import FinancingAlerts from '../FinancingAlerts.vue';

interface Emits {
  (_event: 'cancel-project'): void;
  (_event: 'transfer-titularity'): void;
}

interface Props {
  financing?: UseFinancingDetailsData;
  formalizationInfo?: UseFormalizationInfoData;
  isCancelLoading: boolean;
  loadingProject: boolean;
  projectId?: string;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const projectIdRef = computed(() => props.projectId ?? '');

const resendSignatureQuery = useResendSignature(projectIdRef);

const isThrottled = ref(false);

const enabledResendContractButton = computed(() => props.financing?.data.step === 'biometry_and_contract'
  && props.formalizationInfo?.biometry?.status === 'completed'
  && props.formalizationInfo?.contract?.status === 'waiting');

const baseUrl = import.meta.env.VITE_FINANCING_PLATFORM_API_URL;

function getPlatformUrl(financingParam?: UseFinancingDetailsData): string {
  if (!financingParam?.data?.project_id || !financingParam?.data?.person_type) return baseUrl;

  const routes: Record<string, () => string> = {
    default: () => `${baseUrl}/${
      financingParam.data.person_type === 'PJ'
        ? 'company'
        : 'customer'}/${financingParam.data.project_id}`,
    installation: () => `${baseUrl}/installation/${financingParam.data.project_id}`,
    simulation: () => `${baseUrl}/simulation/result/${financingParam.data.project_id}`,
  };

  const step = financingParam.data.step as keyof typeof routes;

  return (routes[step] || routes.default)();
}

const resendContract = async () => {
  try {
    const result = await resendSignatureQuery.refetch();

    if (result.isError || result.error) {
      const { message, status } = result.error as AxiosError;
      if (status === 404 || status === 500) {
        toast.error('Ocorreu um erro interno, tente novamente mais tarde.');
      } else {
        toast.error(`${message}`);
      }
      return;
    }

    toast.success('Contrato reenviado com sucesso!', {});
  } catch (error) {
    toast.error('Ocorreu um erro interno, tente novamente mais tarde.');
  }
};

const handleResendContract = throttle(async () => {
  isThrottled.value = true;
  await resendContract();
  setTimeout(() => {
    isThrottled.value = false;
  }, 10_000);
}, 10_000, { trailing: false });

const handleGoToPlatform = () => {
  const url = getPlatformUrl(props.financing);
  if (url) window.open(url, '_blank', 'noopener,noreferrer');
};

const handleCancelProject = () => {
  emit('cancel-project');
};
</script>

<template>
  <div class="col-12">
    <div class="financing-header card mb-4">
      <div class="card-body p-4">
        <FinancingAlerts v-if="financing" :financing="financing" />

        <div class="d-flex flex-row align-items-start gap-4 mb-4">
          <div class="flex-grow-1">
            <h2 class="mb-1">{{ financing?.data.customer_name }}</h2>
            <div class="d-flex align-items-center gap-2">
              <span class="text-muted">ID: {{ financing?.data.id }}</span>
              <span
                v-if="financing"
                class="badge"
                :class="`bg-${utils.getStatusColorClass(financing.data.status as FinancingFlowApi.FinancingStatus)}-lt`"
              >
                {{ utils.getStatusLabel(financing.data.status as FinancingFlowApi.FinancingStatus) }}
              </span>
            </div>
          </div>
          <div>
            <FinancingActions
              v-if="financing"
              :enabled-resend-contract="enabledResendContractButton"
              :financing="financing"
              :is-cancel-loading="isCancelLoading"
              :is-throttled="isThrottled"
              :loading-project="loadingProject"
              @cancel-project="handleCancelProject"
              @open-platform="handleGoToPlatform"
              @resend-contract="handleResendContract"
              @transfer-titularity="emit('transfer-titularity')"
            />
          </div>
        </div>

        <hr class="my-3" />

        <div class="row g-3 mb-3">
          <div class="status-card-col-4">
            <StatusCard
              title="Criação"
              :datetime="financing?.data.created_at"
              :icon="IconClock"
              type="informative"
            />
          </div>

          <div class="status-card-col-4">
            <StatusCard
              title="Expiração"
              :datetime="financing?.data.expires_at"
              :icon="IconClockAlert"
              type="warning"
            />
          </div>

          <div class="status-card-col-4">
            <StatusCard
              title="Formalização"
              :datetime="financing?.data.formalized_at || undefined"
              :icon="IconFileCheck"
              :type="financing?.data.formalized_at ? 'positive' : 'default'"
            />
          </div>

          <div class="status-card-col-4">
            <StatusCard
              title="Instalação"
              :datetime="financing?.data.installed_at || undefined"
              :icon="IconCalendarCheck"
              :type="financing?.data.installed_at ? 'positive' : 'default'"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.financing-header {
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 4px 24px 0 rgba(60, 72, 88, 0.08);
  border: none;

  .status-card-col-4 {
    @media (min-width: 768px) {
      flex: 0 0 auto;
      width: 50%;
    }

    @media (min-width: 992px) {
      width: 25%;
    }
  }
}

hr.my-3 {
  border: none;
  border-top: 1.5px solid #e0e3e7;
  margin: 1.5rem 0;
}
</style>
